import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { ArrowLeft, Printer, MapPinned } from "lucide-react";

// Phase 17-C item 6: printable A5 "Invite your street" QR poster.
// Reuses the certificate print CSS (.certificate-print / .no-print) — only
// the poster is visible when printing. QR → the collective's join page.
export default function CollectivePoster() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.getCollective(id).then(setC).catch(() => setNotFound(true)); }, [id]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">This collective doesn't exist or isn't live yet.</div>;
  if (!c) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  const joinUrl = `${window.location.origin}/collectives/${c.id}`;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="collective-poster-page">
      <Seo title={`${c.name} — street poster`} />
      <div className="no-print">
        <Link to={`/collectives/${c.id}`} className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back to {c.name}
        </Link>
      </div>
      <div className="flex items-center justify-between mt-4 no-print">
        <h1 className="font-display text-3xl text-forest tracking-tight">Street poster</h1>
        <Button data-testid="poster-print-btn" onClick={() => window.print()} className="rounded-full bg-forest hover:bg-forest-hover">
          <Printer className="w-4 h-4 mr-2" /> Print (A5)
        </Button>
      </div>
      <p className="text-sm text-neutral-500 mt-2 no-print">
        Pin it on the community noticeboard, the corner shop or the school gate — the QR takes neighbours straight to your join page.
      </p>

      {/* The poster itself — A5 portrait proportions */}
      <div className="certificate-print mt-8 bg-white border-[6px] border-double border-forest rounded-2xl p-10 text-center relative overflow-hidden" style={{ aspectRatio: "148/210" }}>
        <div className="absolute inset-0 pointer-events-none opacity-5" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #1A4331 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative flex flex-col items-center justify-between h-full py-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-bold">Your street is organising</div>
            <div className="font-display text-4xl text-forest mt-4 tracking-tight" data-testid="poster-collective-name">{c.name}</div>
            <div className="text-sm text-neutral-600 mt-2 inline-flex items-center gap-1.5">
              <MapPinned className="w-4 h-4 text-forest/60" /> {c.area_label} · {c.member_count} neighbour{c.member_count !== 1 ? "s" : ""} so far
            </div>
          </div>

          <div className="my-6">
            <div className="inline-block p-4 bg-white border border-neutral-200 rounded-2xl" data-testid="poster-qr">
              <QRCodeSVG value={joinUrl} size={220} fgColor="#1A4331" />
            </div>
            <div className="font-semibold text-forest mt-4">Scan to join your neighbours</div>
            <div className="text-xs text-neutral-500 mt-1">{joinUrl.replace(/^https?:\/\//, "")}</div>
          </div>

          <div>
            <div className="text-sm text-neutral-600 max-w-xs mx-auto leading-relaxed">
              Gatherings, a noticeboard, help when you need a hand — run by neighbours, for neighbours.
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-4">
              Civic<span className="text-terracotta">.</span>Hearth — volunteer locally, ripple globally
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
